import { useCallback, useEffect, useState } from 'react';

export type TextSelectionInfo = {
  text: string;
  pageId: string;
  startOffset: number;
  endOffset: number;
  containerElement: Element | null;
};

type UsePdfPageTextSelectionProps = {
  pageId: string;
  pageInputRef: React.RefObject<HTMLDivElement | null>;
  onTextSelected?: (selectionInfo: TextSelectionInfo) => void;
  onSelectionCleared?: (pageId: string) => void;
};

export const usePdfPageTextSelection = ({
  pageId,
  pageInputRef,
  onTextSelected,
  onSelectionCleared,
}: UsePdfPageTextSelectionProps) => {
  const [lastSelection, setLastSelection] = useState<string>('');

  const extractSelectionInfo = useCallback((): TextSelectionInfo | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }

    const selectedText = selection.toString().trim();
    if (!selectedText) {
      return null;
    }

    const range = selection.getRangeAt(0);

    const containerElement = range.commonAncestorContainer.parentElement;
    // Check if selection is within our PDF page
    if (!pageInputRef.current || !pageInputRef.current.contains(range.commonAncestorContainer)) {
      return null;
    }

    return {
      text: selectedText,
      pageId,
      startOffset: range.startOffset,
      endOffset: range.endOffset,
      containerElement,
    };
  }, [pageId, pageInputRef]);

  const handleSelectionChange = useCallback(() => {
    const selectionInfo = extractSelectionInfo();
    if (selectionInfo && selectionInfo.text.length > 0) {
      setLastSelection(selectionInfo.text);
      onTextSelected?.(selectionInfo);
    } else {
      setLastSelection('');
    }
  }, [extractSelectionInfo, onTextSelected]);

  const handleMouseUp = useCallback(() => {
    // Small delay to ensure selection is finalized
    setTimeout(() => {
      handleSelectionChange();
    }, 10);
  }, [handleSelectionChange]);

  const clearSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
    setLastSelection('');
    onSelectionCleared?.(pageId);
  }, [onSelectionCleared, pageId]);

  useEffect(() => {
    // Both listeners live on `document`, not on `pageInputRef.current`.
    // Attaching to the element used to work only by accident: an
    // inline-callback in the parent kept re-triggering this effect on
    // every parent render, which meant the listeners kept getting
    // re-attached to whatever pageInputRef.current happened to be at the
    // time (react-pdf populates it async, so the first-mount value is
    // frequently null). Now that the effect only runs on mount, we can't
    // depend on the ref being populated then — so we listen at document
    // level and let `extractSelectionInfo` filter to selections that
    // actually belong to this page via `pageInputRef.current.contains`.
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleMouseUp, handleSelectionChange]);

  // Clear selection when component unmounts or pageId changes
  useEffect(() => {
    return () => {
      if (lastSelection) {
        onSelectionCleared?.(pageId);
      }
    };
  }, [pageId, onSelectionCleared, lastSelection]);

  return {
    lastSelection,
    extractSelectionInfo,
    clearSelection,
  };
};
